import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, HandHeart, Users } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLES = [
  { id: "ngo_admin", label: "NGO Admin", desc: "List and manage your organisation", Icon: HandHeart },
  { id: "csr_manager", label: "CSR Manager", desc: "Find compliant partners for CSR spend", Icon: Building2 },
  { id: "donor", label: "Donor / Volunteer", desc: "Discover and support work near you", Icon: Users },
];

export default function Register() {
  const { register } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "ngo_admin", org_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dest = (role) => role === "csr_manager" ? "/csr/workspace" : role === "ngo_admin" ? "/ngo/dashboard" : "/discover";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await register(form);
      toast.success("Account created");
      navigate(dest(user.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-16">
      <div className="bg-white rounded-2xl border border-stone-200 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-brand">{t("common.register")}</h1>
        <p className="text-sm text-stone-500 mt-1">Free, always. Public browsing needs no account.</p>

        <div className="grid sm:grid-cols-3 gap-2 mt-6">
          {ROLES.map((r) => (
            <button key={r.id} type="button" onClick={() => set("role", r.id)} data-testid={`role-${r.id}`}
              className={`text-left p-3 rounded-xl border transition-colors ${form.role === r.id ? "border-terracotta bg-terracotta-light" : "border-stone-200 hover:border-stone-300"}`}>
              <r.Icon size={18} className={form.role === r.id ? "text-terracotta" : "text-stone-500"} />
              <div className="font-semibold text-sm mt-1.5 text-stone-900">{r.label}</div>
              <div className="text-xs text-stone-500 mt-0.5">{r.desc}</div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="register-name-input" className="mt-1.5" />
          </div>
          {(form.role === "ngo_admin" || form.role === "csr_manager") && (
            <div>
              <Label htmlFor="org">{form.role === "ngo_admin" ? "Organisation name" : "Company name"}</Label>
              <Input id="org" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} data-testid="register-org-input" className="mt-1.5" />
            </div>
          )}
          <div>
            <Label htmlFor="remail">Email</Label>
            <Input id="remail" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="register-email-input" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="rpassword">Password (min 6 characters)</Label>
            <Input id="rpassword" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="register-password-input" className="mt-1.5" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-hover" data-testid="register-submit-btn">
            {loading ? "…" : t("common.register")}
          </Button>
        </form>
        <p className="text-sm text-stone-500 mt-5 text-center">
          Already have an account? <Link to="/login" className="text-terracotta font-semibold">{t("common.login")}</Link>
        </p>
      </div>
    </div>
  );
}
